import { useCallback } from 'react';
import { useView } from '../../../contexts/ViewContext';

export const SELECTION_KEYS = {
  org: 'view_selected_org_id',
  client: 'view_selected_client_id',
  project: 'view_selected_project_id',
};

/**
 * Whether a link leads inside this app (a path, or the same origin) rather than to another site.
 * Only in-app tools read the saved organization / client / project selection.
 *
 * @param {*} url
 * @param {string} [origin]  The app's origin; defaults to the current page's
 * @returns {boolean}
 */
export function isInAppLink(url, origin = typeof window !== 'undefined' ? window.location.origin : '') {
  if (typeof url !== 'string' || url.trim() === '' || !origin) return false;
  try {
    const parsed = new URL(url.trim(), origin);
    return /^https?:$/.test(parsed.protocol) && parsed.origin === origin;
  } catch {
    return false;
  }
}

/**
 * Writes the selection the dashboards (AIM, GA4, CDP, ...) open on. The ClientHub client id
 * IS the id of the matching dashboard "client" and "project" (all three come from the same
 * `fn_get_my_clients` rows), and its organization is that row's `company_id`.
 * Written synchronously so a tool opened in a new tab right after reads it on load.
 *
 * @param {{clientId: string, orgId?: string|null, storage?: Storage}} args
 * @returns {boolean} whether the selection was written
 */
export function writeToolSelection({ clientId, orgId = null, storage }) {
  if (!clientId) return false;
  try {
    const store = storage || window.localStorage;
    if (orgId) store.setItem(SELECTION_KEYS.org, String(orgId));
    store.setItem(SELECTION_KEYS.client, String(clientId));
    store.setItem(SELECTION_KEYS.project, String(clientId));
    return true;
  } catch {
    return false; // storage blocked: the tool opens on its own default selection
  }
}

/** `useView()` that yields null outside a ViewProvider (tests, standalone renders) instead of throwing. */
function useOptionalView() {
  try {
    return useView();
  } catch {
    return null;
  }
}

/**
 * Returns `selectForTool()`, to run when an in-app Elsewhere link is opened: it points the
 * dashboards' organization, client and project dropdowns at this client, so the tool opens
 * on it instead of on an empty or stale selection.
 *
 * @param {string} clientId  The ClientHub client being viewed
 * @returns {() => boolean}
 */
export function useToolSelection(clientId) {
  const view = useOptionalView();
  return useCallback(() => {
    if (!clientId) return false;
    const row = (view?.allProjects || []).find((p) => String(p?.id) === String(clientId));
    const orgId = row?.company_id || null;
    const written = writeToolSelection({ clientId, orgId });
    if (view) {
      // selectOrg and selectClient each clear the project, so the project is set last.
      if (orgId) view.selectOrg(String(orgId));
      view.selectClient(String(clientId));
      view.selectProject(String(clientId));
    }
    return written;
  }, [view, clientId]);
}
